"use client";

/**
 * "How many thumbnails per row" — 1·2·3·4 on phones (Gordon 2026-10-31:
 * "selector above to see 1,2,3 or 4 thumbnails on a row. Make 2 default").
 * From tablet width up the grid shows double, so one tile never fills a
 * whole desktop screen. Remembered per viewer (localStorage, best-effort).
 */

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type PerRow = 1 | 2 | 3 | 4;
const OPTIONS: PerRow[] = [1, 2, 3, 4];

// Tailwind needs literal class names.
export const PER_ROW_GRID: Record<PerRow, string> = {
  1: "grid-cols-1 md:grid-cols-2",
  2: "grid-cols-2 md:grid-cols-4",
  3: "grid-cols-3 md:grid-cols-6",
  4: "grid-cols-4 md:grid-cols-8",
};

/** Full section pages: same phone counts, more columns on wide screens. */
export const PER_ROW_GRID_PAGE: Record<PerRow, string> = {
  1: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
  2: "grid-cols-2 md:grid-cols-4 xl:grid-cols-6",
  3: "grid-cols-3 md:grid-cols-6 xl:grid-cols-8",
  4: "grid-cols-4 md:grid-cols-8 xl:grid-cols-10",
};

export function usePerRow(storageKey: string, fallback: PerRow = 2): [PerRow, (n: PerRow) => void] {
  const [n, setN] = useState<PerRow>(fallback);
  useEffect(() => {
    try {
      const v = Number(localStorage.getItem(storageKey));
      if (OPTIONS.includes(v as PerRow)) setN(v as PerRow);
    } catch { /* private mode */ }
  }, [storageKey]);
  const set = (v: PerRow) => {
    setN(v);
    try { localStorage.setItem(storageKey, String(v)); } catch { /* private mode */ }
  };
  return [n, set];
}

export function PerRowPicker({ value, onChange, className, options = OPTIONS }: { value: PerRow; onChange: (n: PerRow) => void; className?: string; options?: PerRow[] }) {
  const t = useTranslations("common");
  return (
    // h-9 = the small Button height, border included, so it lines up with the
    // Share and QR buttons it usually sits beside (Gordon 2026-11-02: "make all
    // buttons same height").
    <div className={cn("flex h-9 shrink-0 overflow-hidden rounded-md border", className)} role="group" aria-label={t("thumbsPerRow")}>
      {options.map((n, i) => (
        <Button
          key={n}
          type="button"
          variant={value === n ? "default" : "ghost"}
          size="icon"
          className={cn("h-full w-9 rounded-none border-0 text-xs font-medium", i > 0 && "border-l")}
          onClick={() => onChange(n)}
          aria-label={t("perRow", { n })}
          aria-pressed={value === n}
        >
          {n}
        </Button>
      ))}
    </div>
  );
}
